import React, {
  useState, useCallback, useEffect, useRef, useMemo,
} from 'react'
import { connect } from 'react-redux'
import { SearchContext } from '../../services/search/SearchContext'
import { SupplementarySearchContext } from '../../services/search/SupplementarySearchContext'
import { reportSearchResults } from '../../services/search/searchReporter'
import {
  closeSearch,
  openSearch,
  registerSearchSurface,
} from '../../services/search/searchSession'
import MiniSearchContentProvider from '../../services/search/providers/MiniSearchContentProvider'
import NavigationMiniSearchProvider from '../../services/search/providers/NavigationMiniSearchProvider'
import ExternalLinksMiniSearchProvider from '../../services/search/providers/ExternalLinksMiniSearchProvider'
import QuickActionsMiniSearchProvider from '../../services/search/providers/QuickActionsMiniSearchProvider'
import {
  DEFAULT_LIMIT_PER_INDEX,
  DISPLAY_PROFILE,
  SHADOW_PROFILES,
} from '../../services/search/local/buildMiniSearchIndices'
import {
  PRESENTATION_ENGINES,
  localProviderId,
  shadowProviderId,
} from '../../services/search/presentationEngine'

// ─── Provider role config ──────────────────────────────────────────────────────
// Extend this list to add/remove providers without changing any consumer code.
// role: 'display'  → results populate SearchContext (shown to the user)
// role: 'shadow'   → results go to the results report only (experiments)
// role: 'additive' → results populate SupplementarySearchContext (supplementary UI slots)
//
// Content search runs MiniSearch once per profile over the same memoised
// indices: DISPLAY_PROFILE is what the user sees, and each SHADOW_PROFILES
// entry is an experiment measured against it on the same query. Each extra pass
// is one in-memory BM25 search over data that is already loaded — a median
// library holds 254 sets_questions documents, p95 775 — so the real budget is
// the report payload, not CPU. See SEARCH_QUALITY_ANALYTICS.md.
//
// Non-content registries (navigation / external-help / quick-actions) land in
// SupplementarySearchContext. PRESENTATION_ENGINE is what consumers resolve
// provider ids through (via localProviderId).
const PRESENTATION_ENGINE = PRESENTATION_ENGINES.MINISEARCH

// When true, every settled query emits a "Minisearch Results Report" and every
// action on it a "Minisearch Results Outcome". Driven by
// REACT_APP_SEARCH_QUALITY_REPORTING so reporting can be switched off per stage
// without a code change; anything other than 'false' leaves it on.
const REPORTING_ENABLED = process.env.REACT_APP_SEARCH_QUALITY_REPORTING !== 'false'

// A query is reported once it has stopped changing for this long, so a user
// typing "fractions" produces one report rather than one per keystroke. The
// content providers are synchronous, so the results are always current; this
// waits for the USER, not for the engines. An action taken sooner flushes the
// report immediately (see registerSearchSurface).
const REPORT_DEBOUNCE_MS = 600

const DISPLAY_PROVIDER_ID = localProviderId('content')

// Which search UI this orchestrator drives. TopNav caps every index at 4 hits
// while the results page asks for 100, so hit counts are only comparable within
// one surface.
function resolveSurface(isTopNav, isSectionHome) {
  if (isTopNav) return 'topNav'
  if (isSectionHome) return 'sectionHome'
  return 'searchResults'
}

function buildProviderConfig() {
  const config = [{
    id: DISPLAY_PROVIDER_ID,
    role: 'display',
    label: 'Library',
    component: MiniSearchContentProvider,
    profile: DISPLAY_PROFILE,
  }]
  SHADOW_PROFILES.forEach((profile) => {
    config.push({
      id: shadowProviderId(profile),
      role: 'shadow',
      label: `Library (${profile})`,
      component: MiniSearchContentProvider,
      profile,
    })
  })
  config.push({
    id: 'navigation-minisearch', role: 'additive', label: 'Navigation', component: NavigationMiniSearchProvider,
  })
  config.push({
    id: 'external-help-minisearch', role: 'additive', label: 'Help & External', component: ExternalLinksMiniSearchProvider,
  })
  config.push({
    id: 'quick-actions-minisearch', role: 'additive', label: 'Quick Actions', component: QuickActionsMiniSearchProvider,
  })
  return config
}

// Built once: the provider set only changes with a code change.
const PROVIDER_CONFIG = buildProviderConfig()

// ─── Options builder ───────────────────────────────────────────────────────────

const TOP_NAV_HITS_PER_PAGE = 4
const SECTION_HOME_HITS_PER_PAGE = 200

function buildSearchOptions(isTopNav, isSectionHome, hitsPerPage) {
  const primaryIndex = isSectionHome ? 'sets_questions' : 'repos'
  // isSectionHome presents only the primary index; all other contexts include
  // both folders and sets_questions alongside repos.
  const secondaryIndexes = isSectionHome ? [] : ['folders', 'sets_questions']
  let resolvedHitsPerPage = hitsPerPage
  if (isTopNav) resolvedHitsPerPage = TOP_NAV_HITS_PER_PAGE
  else if (isSectionHome) resolvedHitsPerPage = SECTION_HOME_HITS_PER_PAGE
  return {
    primaryIndex,
    secondaryIndexes,
    hitsPerPage: resolvedHitsPerPage,
  }
}

// ─── Default context value ─────────────────────────────────────────────────────

const DEFAULT_INDEX_RESULT = {
  hits: [], hitCount: 0, hasMore: false, loadMore: () => {},
}

const DEFAULT_RESULTS = {
  suggestions: [],
  hits: [],
  indexResults: {
    repos: { ...DEFAULT_INDEX_RESULT },
    folders: { ...DEFAULT_INDEX_RESULT },
    // eslint-disable-next-line camelcase
    sets_questions: { ...DEFAULT_INDEX_RESULT },
  },
}

// ─── SearchOrchestrator ────────────────────────────────────────────────────────

/**
 * Owns query state and renders all search providers as headless siblings.
 * Routes each provider's results to the correct context based on its role.
 *
 * Consumer components (TopNavSearchBar, SearchResultsContainer, etc.) read only
 * from SearchContext or SupplementarySearchContext — they have no knowledge of
 * which providers are active.
 */
function SearchOrchestrator({
  libraryLoadStatus,
  isTopNav,
  isSectionHome,
  hitsPerPage,
  searchState,
  onSearchStateChange,
  children,
}) {
  const [query, setQuery] = useState(() => (searchState && searchState.query) || '')
  const [displayResults, setDisplayResults] = useState(DEFAULT_RESULTS)
  const [additiveResults, setAdditiveResults] = useState([])
  const [shadowResults, setShadowResults] = useState([])

  // Sync query when the URL-driven searchState prop changes (e.g. browser back).
  // React bails out of a re-render when setQuery is called with the same value,
  // so there is no risk of an infinite loop with the onSearchStateChange effect.
  useEffect(() => {
    const externalQuery = (searchState && searchState.query) || ''
    setQuery(externalQuery)
  }, [searchState])

  // Notify the parent (e.g. SearchResultsContainer) when the query changes so
  // it can debounce URL updates. Use a ref so onSearchStateChange can change
  // without this effect becoming stale.
  const onSearchStateChangeRef = useRef(onSearchStateChange)
  onSearchStateChangeRef.current = onSearchStateChange
  useEffect(() => {
    if (onSearchStateChangeRef.current) {
      onSearchStateChangeRef.current({ query })
    }
  }, [query])

  // Route results based on the provider's role.
  //
  // Additive blocks carry both the flat `hits` list and the optional
  // `indexResults` object; lightweight additive providers (navigation,
  // external-help) leave `indexResults` undefined and consumers read flat `hits`.
  const handleResults = useCallback((role, providerId, profile, results) => {
    if (role === 'display') {
      // Merged onto the default shape so a provider that omits a field cannot
      // hand `undefined` to consumers that read `indexResults.repos.hitCount`.
      setDisplayResults({
        ...DEFAULT_RESULTS,
        ...results,
        indexResults: results.indexResults || DEFAULT_RESULTS.indexResults,
      })
    } else if (role === 'additive') {
      setAdditiveResults((prev) => {
        const without = prev.filter((r) => r.providerId !== providerId)
        return [...without, {
          providerId,
          hits: results.hits || [],
          indexResults: results.indexResults,
        }]
      })
    } else if (role === 'shadow') {
      setShadowResults((prev) => {
        const without = prev.filter((r) => r.providerId !== providerId)
        return [...without, {
          providerId,
          profile,
          query: results.query,
          indexResults: results.indexResults,
        }]
      })
    }
  }, [])

  // Stable references — providers receive the same options object identity
  // until their inputs actually change, preventing cascading re-renders.
  const searchOptions = useMemo(
    () => buildSearchOptions(isTopNav, isSectionHome, hitsPerPage),
    [isTopNav, isSectionHome, hitsPerPage],
  )

  // Memoised per-provider onResults callback. Identity is stable for the
  // lifetime of the orchestrator since handleResults is stable.
  const onResultsCacheRef = useRef(new Map())
  const getOnResults = (role, id, profile) => {
    const key = `${role}:${id}`
    if (!onResultsCacheRef.current.has(key)) {
      onResultsCacheRef.current.set(key, (results) => handleResults(role, id, profile, results))
    }
    return onResultsCacheRef.current.get(key)
  }

  const searchContextValue = useMemo(
    () => ({
      ...displayResults,
      currentRefinement: query,
      refine: setQuery,
    }),
    [displayResults, query],
  )

  const supplementaryContextValue = useMemo(
    () => ({ additiveResults, presentationEngine: PRESENTATION_ENGINE }),
    [additiveResults],
  )

  // Context reported with every results report so the rows can be sliced
  // without guessing which UI produced them or which cap was in force.
  const surface = useMemo(
    () => resolveSurface(isTopNav, isSectionHome),
    [isTopNav, isSectionHome],
  )
  const limits = useMemo(
    () => ({
      hitsPerPage: searchOptions.hitsPerPage,
      minisearchLimitPerIndex: searchOptions.hitsPerPage || DEFAULT_LIMIT_PER_INDEX,
    }),
    [searchOptions],
  )

  // ─── Results report + search session ─────────────────────────────────────
  //
  // Exactly one report per settled query. `emitReportRef` is reassigned on
  // every render so whichever path fires it — the debounce, or an action
  // flushing it early — reads the current results. It is idempotent per query,
  // which is what lets an action call it unconditionally.
  const reportedQueryRef = useRef(null)
  const emitReportRef = useRef(() => {})
  emitReportRef.current = () => {
    if (!REPORTING_ENABLED || !query) return
    if (reportedQueryRef.current === query) return
    // Every provider has already answered this query, or the report would
    // describe the previous one. The providers emit synchronously in the same
    // commit as the query change, so this only fails inside that commit.
    if (displayResults.query !== query) return
    const shadows = SHADOW_PROFILES
      .map((profile) => shadowResults.find((b) => b.profile === profile))
      .filter((b) => b && b.query === query)
      .map((b) => ({ profile: b.profile, indexResults: b.indexResults }))
    reportedQueryRef.current = query
    const searchId = openSearch(surface, {
      query,
      displayProfile: DISPLAY_PROFILE,
      displayIndexResults: displayResults.indexResults,
      shadows,
    })
    reportSearchResults({
      searchId,
      query,
      surface,
      limits,
      displayProfile: DISPLAY_PROFILE,
      displayIndexResults: displayResults.indexResults,
      shadows,
      indexSizes: displayResults.indexSizes,
      additiveResults,
      loadStatus: libraryLoadStatus,
    })
  }

  // Lets an action on this surface force the pending report out first.
  useEffect(() => {
    if (!REPORTING_ENABLED) return undefined
    return registerSearchSurface(surface, () => emitReportRef.current())
  }, [surface])

  // A new query ends the previous search. Unmounting ends it too — leaving the
  // results page or closing the modal without acting is abandoning it.
  useEffect(() => {
    if (!REPORTING_ENABLED) return
    closeSearch(surface, { nextQuery: query })
  }, [surface, query])
  useEffect(() => () => closeSearch(surface), [surface])

  useEffect(() => {
    if (!REPORTING_ENABLED || !query) return undefined
    if (reportedQueryRef.current === query) return undefined
    const handle = setTimeout(() => emitReportRef.current(), REPORT_DEBOUNCE_MS)
    return () => clearTimeout(handle)
  }, [query])

  return (
    <SearchContext.Provider value={searchContextValue}>
      <SupplementarySearchContext.Provider value={supplementaryContextValue}>
        {PROVIDER_CONFIG.map(({
          id, role, profile, component: Provider,
        }) => (
          <Provider
            key={id}
            query={query}
            options={searchOptions}
            profile={profile}
            onResults={getOnResults(role, id, profile)}
          />
        ))}
        {children}
      </SupplementarySearchContext.Provider>
    </SearchContext.Provider>
  )
}

function mapStateToProps(state) {
  return {
    // What each library collection was supposed to load and whether it did.
    // Reported with every results report so an incomplete library is visible.
    libraryLoadStatus: state.libraryLoadStatus,
  }
}

export default connect(
  mapStateToProps,
  {},
)(SearchOrchestrator)
